import { useEffect } from 'react';

/**
 * Single-key layer toggles, e.g. "c" for Correct or "1" for Tags: missing.
 *
 * Pass `null` for `shortcuts` to disable (e.g. when the owning tab is not
 * active). Keys are matched lowercase, so the config's shortcut must be too.
 */
export function useLayerShortcuts(
  shortcuts: Map<string, string> | null,
  setEnabledLayers: React.Dispatch<React.SetStateAction<Set<string>>>
) {
  useEffect(() => {
    if (!shortcuts) {
      return;
    }

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) {
        return;
      }

      // Never steal a keystroke meant for a search box or other field.
      const target = e.target as HTMLElement | null;
      if (
        target?.isContentEditable ||
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(target?.tagName ?? '')
      ) {
        return;
      }

      const layer = shortcuts.get(e.key.toLowerCase());
      if (!layer) {
        return;
      }

      e.preventDefault();
      setEnabledLayers((prev) => {
        const next = new Set(prev);
        if (next.has(layer)) {
          next.delete(layer);
        } else {
          next.add(layer);
        }
        return next;
      });
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [shortcuts, setEnabledLayers]);
}

/** Builds a shortcut -> layer-key lookup from one or more layer configs. */
export function buildLayerShortcuts(
  ...configs: { key: string; shortcut: string }[][]
): Map<string, string> {
  return new Map(configs.flat().map((layer) => [layer.shortcut, layer.key]));
}
